import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { ProjectStorage } from '../../core/project.storage';
import { DcBaseRequester } from './dc-base.requester';
import { IDcTaskResponse, ITaskTypeMappingResponse } from './models/task.model';

@Injectable({
	providedIn: 'root',
})
export class TaskRequester extends DcBaseRequester<IDcTaskResponse> {
	protected resource: string;
	protected elementsListUrl: string;
	protected singleElementUrl: string;

	public constructor(
		private readonly urlService: UrlService,
		private readonly userSettings: ProjectStorage
	) {
		super();
		this.resource = this.generateResourceUrl('SPARK_JOBS');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getTaskList(
		viewActiveJobs: boolean,
		useCache: boolean
	): Observable<ReadonlyArray<IDcTaskResponse>> {
		const url = this.urlService.generateUrl(
			this.resource,
			{},
			{
				project: this.projectId,
				onlyRunning: viewActiveJobs,
				ctx: this.userSettings.getSelectedContext(),
			}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					evictTimeoutInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.CacheOnly,
					evictTimeoutInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IDcTaskResponse>>(url, cachePolicy);
	}

	public killAllJobs(): Observable<void> {
		const resourceUrl = this.generateResourceUrl('SPARK_JOBS_KILL_ALL');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				ctx: this.userSettings.getSelectedContext(),
			}
		);

		return this.get<void>(url);
	}

	public clearCacheJobs(): Observable<void> {
		const resourceUrl = this.generateResourceUrl('SPARK_JOBS_CLEAR_JOBS');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				ctx: this.userSettings.getSelectedContext(),
			}
		);

		return this.get<void>(url);
	}

	public getAvailableTaskTypes(): Observable<
		ReadonlyArray<ITaskTypeMappingResponse>
	> {
		const resourceUrl = this.generateResourceUrl('JOB_TYPES');

		const url = this.urlService.generateUrl(resourceUrl, {}, {});

		return this.get<ReadonlyArray<ITaskTypeMappingResponse>>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.CacheOnly,
				evictTimeoutInSeconds: 30 * 60,
				isCacheable: true,
			})
		);
	}

	public killJob(jobId: string): Observable<void> {
		const resourceUrl = this.generateResourceUrl('SPARK_JOB');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				jobKey: jobId,
			},
			{
				project: this.projectId,
				ctx: this.userSettings.getSelectedContext(),
			}
		);
		return this.http.delete<void>(url);
	}
}
